import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Hub } from 'aws-amplify';
import once from 'lodash/once';
import PropTypes from 'prop-types';
import { isEmpty } from 'rambdax';
import { useSWRConfig } from 'swr';
import PublicRoutes from '@/constants/routing/PublicRoutes';
import { RESOURCE_NOT_FOUND, UNAUTHORIZED } from '@/lib/constants/error-enums';
import {
  OWNER_ORG_ID_PUB,
  USER_ID_PUB,
} from '@/lib/constants/localStorageKeys';
import { useGoogleAnalytics } from '@/modules/GoogleAnalytics';
import userActionTypes from '@/rdx/actionTypes';
import { userService } from '@/services/user';

const googleProviderString = 'google';

// Array of errors that, when encountered from a valid google auth, we need to
// direct the user to registration so they can create a user.
const REGISTRATION_NEEDED_ERRORS = [UNAUTHORIZED, RESOURCE_NOT_FOUND];

export default function Authentication({ children }) {
  const { mutate } = useSWRConfig();
  const { resetAnalyticsState, sendEvent, setUserInfo } = useGoogleAnalytics();

  // The setting of the user's info is done by an event that gets called from
  // Amplify's Hub. It can happen either at login or when the user returns to the
  // page without having to log in due to having a saved session. When that
  // happens, the "tokenRefresh" event is called. However "tokenRefresh" is an
  // event that is continuously called throughout a user's session. Therefore, we
  // only want to call `setUserInfo` once, either at "signIn" or the first
  // "tokenRefresh" event.
  //
  // We also use a ref so that we can reset this "once" call for the use case
  // for when a user signs out and signs back in during the same session.
  const setUserInfoOnceRef = React.useRef(once(setUserInfo));

  const dispatch = useDispatch();
  const history = useHistory();

  function handleAdminRegistration(regAdminData) {
    dispatch({
      type: userActionTypes.SET_USER_REG_ADMIN_DATA,
      data: regAdminData,
    });

    history.push(PublicRoutes.ADMIN_REGISTER);

    return null;
  }

  React.useEffect(() => {
    Hub.listen('auth', (data) => {
      const { payload } = data;
      const { message } = payload;
      const isFromGoogle = message.includes(googleProviderString);

      if (payload.event === 'signIn') {
        userService.setAccessIdTokenFromCognitoUserResponse(payload?.data);

        userService
          .getContext(null, { isFromLogin: true })
          .then((contextResponse) => {
            // This handles admin registration coming from federated cognito google authentication
            if (
              isFromGoogle &&
              isEmpty(contextResponse?.data?.context?.contexts)
            ) {
              return handleAdminRegistration(payload?.data);
            }

            // check for a non-registered user login
            // Handle normal login condition
            const userSession = userService.buildUserSessionObject(
              payload?.data,
              contextResponse,
            );
            const tokenExpiration =
              payload?.data?.signInUserSession?.idToken?.payload?.exp;

            const userResponse = { userSession, tokenExpiration };

            sendEvent('login');
            setUserInfoOnceRef.current({
              userId: userResponse?.userSession?.user?.userId,
              ownerOrgId: userResponse?.userSession?.org?.ownerOrgId,
            });
            dispatch({ type: userActionTypes.LOGIN_SUCCESS, userResponse });
            dispatch({
              type: userActionTypes.REFRESH_ACCESS_TOKEN_TIMER_SETUP,
            });

            return userResponse;
          })
          .catch((errorResponse) => {
            // This handles admin registration coming from federated cognito google authentication
            if (
              isFromGoogle &&
              REGISTRATION_NEEDED_ERRORS.includes(errorResponse?.errorEnum)
            ) {
              return handleAdminRegistration(payload?.data);
            }

            // Handle context error condition
            if (
              typeof errorResponse === 'undefined' ||
              (errorResponse && errorResponse.error)
            ) {
              // eslint-disable-next-line no-console
              console.warn('Context error, fail login');

              const error = { message: 'No user context found.' };
              dispatch({ type: userActionTypes.LOGIN_FAILURE, error });
              return userService.logout();
            }

            return errorResponse;
          });
      } else if (payload.event === 'tokenRefresh') {
        const userInfo = {
          userId: localStorage.getItem(USER_ID_PUB),
          ownerOrgId: localStorage.getItem(OWNER_ORG_ID_PUB),
        };

        setUserInfoOnceRef.current(userInfo);
      } else if (payload.event === 'signOut') {
        resetAnalyticsState();

        // clear swr cache
        mutate(() => true, undefined, { revalidate: false });

        // When we sign out, we need to reset the setUserInfoOnceRef so that it
        // potentially can record subsequent logins.
        setUserInfoOnceRef.current = once(setUserInfo);
      }
    });
  }, []);

  return children;
}

Authentication.propTypes = {
  children: PropTypes.node.isRequired,
};
